import React, { useState, useContext, createContext, useCallback, useMemo, useRef } from 'react';
import api, { setCsrfToken } from '../services/api';
import toast from 'react-hot-toast';

// Context dla autoryzacji
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Provider autoryzacji
// BEZPIECZENSTWO: Dane usera trzymane TYLKO w React state + httpOnly session cookie.
// NIE uzywamy localStorage dla danych auth (podatne na XSS).
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // true = czeka na sprawdzenie sesji
  const autoLogoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  const login = useCallback(async (credentials) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/login', credentials);
      const userData = response.data.data;
      // Ustaw CSRF token z odpowiedzi login
      if (userData.csrf_token) {
        setCsrfToken(userData.csrf_token);
      }
      // Anti-lockout: zapisz serwerowo wygenerowany device_token
      if (userData.device_token) {
        localStorage.setItem('v3sys_device_token', userData.device_token);
      }
      setUser(userData);
      // Ustaw flage sesji przegladarki (wygasa przy zamknieciu)
      sessionStorage.setItem('v3sys_browser_session', '1');
      // Migracja: usun stare dane z localStorage jesli istnieja
      localStorage.removeItem('user');
      return { success: true, user: userData };
    } catch (error) {
      // Trusted Devices — obsługa kodów błędów urządzenia
      if (error.errorCode === 'DEVICE_NOT_TRUSTED') {
        if (error.deviceToken) {
          localStorage.setItem('v3sys_device_token', error.deviceToken);
        }
        return { success: false, error: error.message, errorCode: 'DEVICE_NOT_TRUSTED' };
      }
      if (error.errorCode === 'DEVICE_PENDING') {
        return { success: false, error: error.message, errorCode: 'DEVICE_PENDING' };
      }
      if (error.errorCode === 'DEVICE_BLOCKED') {
        return { success: false, error: error.message, errorCode: 'DEVICE_BLOCKED' };
      }
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Logout zawsze udany lokalnie
    } finally {
      setUser(null);
      setCsrfToken(null);
      localStorage.removeItem('user');
      // Lokalizacja jest wybierana na nowo przy kazdym logowaniu (dla kasjera
      // to JEDYNY moment wyboru — w trakcie sesji jest zablokowana)
      localStorage.removeItem('selectedLocation');
      sessionStorage.removeItem('v3sys_browser_session');
      sessionStorage.removeItem('v3sys_device_expires_at');
      if (autoLogoutTimerRef.current) clearTimeout(autoLogoutTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      setLoading(false);
    }
    return { success: true };
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/check');
      const authData = response.data.data;

      if (authData.authenticated) {
        // Ustaw CSRF token z odpowiedzi checkAuth
        if (authData.csrf_token) {
          setCsrfToken(authData.csrf_token);
        }
        setUser({
          user_id: authData.user_id,
          login: authData.login,
          user_type: authData.user_type,
          logged_in_at: authData.logged_in_at
        });
        return true;
      } else {
        setUser(null);
        return false;
      }
    } catch (error) {
      setUser(null);
      return false;
    }
  }, []);

  // Auto-logout timer dla sesji QR (device_expires_at)
  const setupAutoLogout = useCallback(() => {
    // Wyczyść poprzednie timery
    if (autoLogoutTimerRef.current) clearTimeout(autoLogoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);

    const expiresAt = sessionStorage.getItem('v3sys_device_expires_at');
    if (!expiresAt) return;

    const ms = new Date(expiresAt).getTime() - Date.now();

    if (ms <= 0) {
      // Sesja już wygasła — natychmiast wyloguj
      sessionStorage.removeItem('v3sys_device_expires_at');
      logout().then(() => {
        window.location.href = '/login?reason=session_expired';
      });
      return;
    }

    // Warning 5 min przed wygaśnięciem
    const warningMs = ms - 5 * 60 * 1000;
    if (warningMs > 0) {
      warningTimerRef.current = setTimeout(() => {
        const minutesLeft = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 60000);
        toast('Sesja wygasa za ' + minutesLeft + ' min', {
          duration: 10000,
          icon: '⚠️',
        });
      }, warningMs);
    }

    // Auto-logout po wygaśnięciu
    autoLogoutTimerRef.current = setTimeout(() => {
      sessionStorage.removeItem('v3sys_device_expires_at');
      logout().then(() => {
        window.location.href = '/login?reason=session_expired';
      });
    }, ms);
  }, [logout]);

  // Sprawdz autoryzacje przy zaladowaniu - wylacznie przez serwer (httpOnly cookie)
  React.useEffect(() => {
    const initAuth = async () => {
      // Migracja: usun stare dane z localStorage
      localStorage.removeItem('user');

      // Sprawdz czy to nowa sesja przegladarki (sessionStorage wygasa przy zamknieciu)
      const browserSessionActive = sessionStorage.getItem('v3sys_browser_session');

      if (!browserSessionActive) {
        // Nowa sesja przegladarki - wyloguj nawet jesli cookie istnieje
        try {
          await api.post('/auth/logout');
        } catch (e) {
          // Ignoruj bledy logout
        }
        setUser(null);
        sessionStorage.removeItem('v3sys_device_expires_at');
        setLoading(false);
        return;
      }

      // Sprawdz sesje na serwerze (httpOnly cookie)
      const isAuthenticated = await checkAuth();
      setLoading(false);

      // Jeśli zalogowany i jest device_expires_at — ustaw auto-logout timer
      if (isAuthenticated) {
        setupAutoLogout();
      }
    };

    initAuth();

    // Heartbeat — sprawdzaj sesję co 5 minut (odświeża CSRF token + wykrywa wygaśnięcie)
    const heartbeatInterval = setInterval(async () => {
      const browserSession = sessionStorage.getItem('v3sys_browser_session');
      if (!browserSession) return;
      try {
        const isOk = await checkAuth();
        if (!isOk) {
          console.warn('[Heartbeat] Sesja wygasła na serwerze');
          setUser(null);
          window.location.href = '/login?reason=session_expired';
        }
      } catch (e) {
        console.warn('[Heartbeat] Błąd sprawdzania sesji:', e.message);
      }
    }, 5 * 60 * 1000); // 5 minut

    // Cleanup timerów przy unmount
    return () => {
      if (autoLogoutTimerRef.current) clearTimeout(autoLogoutTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      clearInterval(heartbeatInterval);
    };
  }, [checkAuth, setupAutoLogout]);

  // Memoizacja value — zapobiega niepotrzebnym re-renderom konsumentow
  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.user_type === 'admin'
  }), [user, loading, login, logout, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
